import React from "react";
import { useDispatch } from "react-redux";
import { Modal } from "@/components/modal/modal";
import { closeModal } from "libs/modals";
import { FileToUpload } from "api/media-library";
import { ADD_FRONTIFY_MODAL } from ".";
import { toast } from "react-toastify";
import styled from "styled-components";
import { useTrackEvent } from "libs/analytics";
import { ASSETS_IMPORTED_FRONTIFY } from "constants/Events";

const FRONTIFY_FINDER_SCRIPT_SRC =
  "https://unpkg.com/@frontify/frontify-finder@2.0.1/dist/index.js";
const FRONTIFY_FINDER_SCRIPT_SELECTOR =
  'script[src*="@frontify/frontify-finder"]';
const FRONTIFY_FINDER_SCRIPT_ID = "frontify-finder-sdk";
const FRONTIFY_FINDER_READY_TIMEOUT_MS = 10_000;
const FRONTIFY_FINDER_POLL_INTERVAL_MS = 100;

let frontifyFinderReadyPromise: Promise<void> | null = null;

interface FrontifyAssetTag {
  value: string;
}

interface FrontifyAsset {
  filename: string;
  downloadUrl: string;
  type: string;
  tags?: FrontifyAssetTag[];
  description?: string;
  title?: string;
  extension?: string;
}

const hasFrontifyFinderCreate = () =>
  typeof window.FrontifyFinder?.create === "function";

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

const getOrCreateFrontifyFinderScript = (): HTMLScriptElement => {
  const existingScript =
    document.querySelector<HTMLScriptElement>(
      `script#${FRONTIFY_FINDER_SCRIPT_ID}`
    ) ||
    document.querySelector<HTMLScriptElement>(FRONTIFY_FINDER_SCRIPT_SELECTOR);

  if (existingScript) {
    return existingScript;
  }

  const script = document.createElement("script");
  script.id = FRONTIFY_FINDER_SCRIPT_ID;
  script.src = FRONTIFY_FINDER_SCRIPT_SRC;
  document.head.append(script);

  return script;
};

const waitForFrontifyFinder = () =>
  new Promise<void>((resolve, reject) => {
    if (hasFrontifyFinderCreate()) {
      resolve();
      return;
    }

    const start = Date.now();
    const interval = window.setInterval(() => {
      if (hasFrontifyFinderCreate()) {
        window.clearInterval(interval);
        resolve();
        return;
      }

      const hasTimedOut =
        Date.now() - start >= FRONTIFY_FINDER_READY_TIMEOUT_MS;
      if (hasTimedOut) {
        window.clearInterval(interval);
        reject(new Error("Frontify SDK failed to load"));
      }
    }, FRONTIFY_FINDER_POLL_INTERVAL_MS);
  });

const ensureFrontifyFinderReady = async () => {
  if (hasFrontifyFinderCreate()) {
    return;
  }

  if (!frontifyFinderReadyPromise) {
    frontifyFinderReadyPromise = (async () => {
      getOrCreateFrontifyFinderScript();
      await waitForFrontifyFinder();
    })().catch((error) => {
      frontifyFinderReadyPromise = null;
      throw error;
    });
  }

  await frontifyFinderReadyPromise;
};

interface FrontifyMediaModalProps {
  limit: number;
  onSelect(files: FileToUpload[]): void | Promise<void>;
}

export const FrontifyMediaModal: React.FC<FrontifyMediaModalProps> = ({
  limit,
  onSelect,
}) => {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  function closeFrontifyModal() {
    dispatch(closeModal({ type: ADD_FRONTIFY_MODAL }));
  }

  const initFrontify = async () => {
    try {
      await ensureFrontifyFinderReady();

      const element =
        document.querySelector<HTMLDivElement>("#frontify-finder");
      if (!element) {
        toast.error("Failed to find the Frontify element in the DOM");
        return;
      }

      const finder = await window.FrontifyFinder.create({
        clientId: "skedsocial",
        options: {
          allowMultiSelect: true,
        },
      });

      if (!finder) {
        toast.error("Failed to initialize Frontify Finder");
        dispatch(closeModal({ type: ADD_FRONTIFY_MODAL }));
        return;
      }

      finder.mount(element);
      finder.onAssetsChosen(async (assets: FrontifyAsset[]) => {
        const files: FileToUpload[] = assets.slice(0, limit).map((asset) => ({
          contentType: "url",
          name: asset.title
            ? asset.extension
              ? `${asset.title}.${asset.extension}`
              : asset.title
            : asset.filename,
          url: asset.downloadUrl,
          tag: asset.tags ? asset.tags.map((perTag) => perTag.value) : [],
          description: asset.description,
          type: asset.type.toLowerCase().includes("image")
            ? "image"
            : asset.type.toLowerCase().includes("video")
              ? "video"
              : "document",
        }));
        try {
          trackEvent({
            eventName: ASSETS_IMPORTED_FRONTIFY,
            useServices: ["AMP"],
          });

          await onSelect(files);
          dispatch(closeModal({ type: ADD_FRONTIFY_MODAL }));
        } catch (error: unknown) {
          toast.error(
            `Error processing selected files: ${getErrorMessage(error)}`
          );
        }
      });
      finder.onCancel(() => closeFrontifyModal());
    } catch (error: unknown) {
      toast.error(`Unexpected error: ${getErrorMessage(error)}`);
      dispatch(closeModal({ type: ADD_FRONTIFY_MODAL }));
    }
  };

  return (
    <div>
      <Modal
        data-testid="add-user-modal"
        onShow={async () => {
          await initFrontify();
        }}
        show
        onHide={closeFrontifyModal}
        animation={false}
        bsSize="lg"
      >
        <Modal.Header closeButton onHide={closeFrontifyModal}>
          <Modal.Title>Import Frontify Media Files</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FrontifyFinder id="frontify-finder"></FrontifyFinder>
        </Modal.Body>
      </Modal>
    </div>
  );
};

const FrontifyFinder = styled.div`
  height: 80vh;
`;
